// 📄 pages/Admin/components/ProgramaFormModal.tsx
import React, { useEffect, useState } from 'react';
import { X, Save } from 'lucide-react';
import {
  Programa,
  ProgramaData,
  SolucionPrograma,
  SOLUCIONES_PROGRAMA,
  savePrograma
} from '../../../services/catalogoProgramasService.ts';
import { getBadis } from '../../../services/catalogoBadisService.ts';
import { getTransacciones } from '../../../services/catalogoTransaccionesService.ts';
import CatalogoMultiSelect from '../../../components/ui/CatalogoMultiSelect.tsx';

interface ProgramaFormModalProps {
  programa: Programa | null; // null = alta nueva
  onClose: () => void;
  onSaved: () => void;
}

const generarId = (): string => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const EMPTY_FORM: ProgramaData = {
  nombre: '',
  nombreTecnico: '',
  solucion: 'general',
  descripcion: '',
  badiRefIds: [],
  transaccionRefIds: []
};

export default function ProgramaFormModal({ programa, onClose, onSaved }: ProgramaFormModalProps) {
  const [form, setForm] = useState<ProgramaData>(
    programa
      ? {
          nombre: programa.nombre,
          nombreTecnico: programa.nombreTecnico,
          solucion: programa.solucion,
          descripcion: programa.descripcion,
          badiRefIds: programa.badiRefIds,
          transaccionRefIds: programa.transaccionRefIds
        }
      : EMPTY_FORM
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Catálogos existentes, para armar las opciones de los 2 selectores de
  // relaciones N:N — se cargan una sola vez al abrir el modal.
  const [badisOpciones, setBadisOpciones] = useState<{ id: string; label: string }[]>([]);
  const [transaccionesOpciones, setTransaccionesOpciones] = useState<{ id: string; label: string }[]>([]);

  useEffect(() => {
    getBadis()
      .then((lista) => setBadisOpciones(lista.map((b) => ({ id: b.id, label: b.nombre }))))
      .catch((err) => console.error('Error cargando el catálogo de BAdIs para relaciones:', err));
    getTransacciones()
      .then((lista) => setTransaccionesOpciones(lista.map((t) => ({ id: t.id, label: `${t.transaccion} — ${t.nombre}` }))))
      .catch((err) => console.error('Error cargando el catálogo de Transacciones para relaciones:', err));
  }, []);

  const handleChange = (field: keyof ProgramaData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.nombre.trim()) {
      setError('El nombre del programa es obligatorio.');
      return;
    }

    setSaving(true);
    try {
      await savePrograma(programa?.id || generarId(), form, !programa);
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Error al guardar el registro.');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 className="text-lg font-medium text-white">
            {programa ? 'Editar Programa' : 'Nuevo Programa'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
          )}

          <div>
            <label className={labelClass}>Nombre <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={form.nombre}
              onChange={(e) => handleChange('nombre', e.target.value)}
              placeholder="ej. Include HES desde ERP"
              className={inputClass}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Nombre técnico</label>
              <input
                type="text"
                value={form.nombreTecnico}
                onChange={(e) => handleChange('nombreTecnico', e.target.value)}
                placeholder="ej. LMLSRF0F"
                className={`${inputClass} font-mono`}
              />
            </div>
            <div>
              <label className={labelClass}>Solución / Canal</label>
              <select
                className={inputClass}
                value={form.solucion}
                onChange={(e) => handleChange('solucion', e.target.value as SolucionPrograma)}
              >
                {SOLUCIONES_PROGRAMA.map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className={labelClass}>Descripción / Propósito</label>
            <textarea
              value={form.descripcion}
              onChange={(e) => handleChange('descripcion', e.target.value)}
              placeholder="Para qué sirve este programa dentro de la integración..."
              rows={3}
              className={`${inputClass} resize-none`}
            />
          </div>

          <div className="pt-2 border-t border-white/10 space-y-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Relaciones</p>
            <CatalogoMultiSelect
              label="BAdIs / Objetos ABAP relacionados"
              opciones={badisOpciones}
              seleccionados={form.badiRefIds}
              onChange={(ids) => setForm((prev) => ({ ...prev, badiRefIds: ids }))}
              placeholder="Agregar BAdI/objeto relacionado..."
            />
            <CatalogoMultiSelect
              label="Transacciones relacionadas"
              opciones={transaccionesOpciones}
              seleccionados={form.transaccionRefIds}
              onChange={(ids) => setForm((prev) => ({ ...prev, transaccionRefIds: ids }))}
              placeholder="Agregar transacción relacionada..."
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-600 px-4 py-2 rounded-lg text-sm font-semibold text-[#fff] transition-all"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
